/* eslint-disable no-case-declarations */
/* eslint-disable react-hooks/exhaustive-deps */
import { QueryStatus } from '@reduxjs/toolkit/query'
import { diff } from 'deep-object-diff'
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useGetIsInFreezePeriodQuery } from '@Store/api/dynamic-pricing'
import {
  useLazyProductsQuery,
  useListSavedProductFiltersQuery,
  useTotalProductCountQuery,
} from '@Store/api/product'
import type {
  FilterFormType,
  PaginatedQueryParams,
  PaginatedResponse,
  ProductsFilterType,
  ProductsRequestParamsType,
} from '@Store/types'
import type { Filter, Product } from '@Store/types/product'
import { FastFetchModalProvider } from '@Utils/context/modal/use-fast-fetch-modal'
import { AlreadyReportedUrlProvider } from '@Utils/context/modal/UseAlreadyReportedUrl'
import { useDebugMode } from '@Utils/context/modal/useDebugMode'
import { DeleteProductProvider } from '@Utils/context/modal/useDeleteProduct'
import { EditProductProvider } from '@Utils/context/modal/useEditProduct'
import { RemoveUrlProvider } from '@Utils/context/modal/useRemoveUrl'
import { ReportUrlProvider } from '@Utils/context/modal/useReportUrl'
import { ToggleExclusionProvider } from '@Utils/context/modal/useToggleExclusion'
import { IntercomEvent } from '@Utils/helpers/getIntercomMsg'
import scrollToId from '@Utils/helpers/scroll-to-id'
import useIntercomInternal from '@Utils/hooks/use-intercom-internal'

import NumberedPagination from '../../../components/common/pagination/numbered'
import DeleteProductModal from '../../../components/product/options/delete-product-modal'
import EditProductModal from '../../../components/product/options/edit-product-modal'
import ProductHeader from '../../../components/product/product-header/ProductHeader'
import ProductSearch from '../../../components/product/product-search'
import fixFilterFieldValues from '../../../components/product/product-search/fixFilterFieldValues'
import nonFilterFields from '../../../components/product/product-search/nonFilterFields'
import AddUrlFastFetchModal from '../../../components/product/product-table-actions/add-url-fast-fetch-modal'
import AlreadyReportedModal from '../../../components/product/product-table-actions/already-reported-modal'
import RemoveUrlModal from '../../../components/product/product-table-actions/remove-url'
import ReportUrlModal from '../../../components/product/product-table-actions/report-url'
import ToggleExclusionModal from '../../../components/product/product-table-actions/toggle-exclusion-modal'
import ProductsList from '../../../components/product/ProductsList'
import ProductsSidebar from '../../../components/product/ProductsSidebar'
import Wrapper from '../../../layouts/wrapper'

const initialRequestParams: ProductsRequestParamsType = {
  page: 1,
  limit: 10,
}

function Product(): JSX.Element {
  const navigate = useNavigate()
  const { isDebug, debugParam } = useDebugMode()
  const { search: queryString } = useLocation()
  const { trackEventInternal } = useIntercomInternal()

  const [
    fetchProducts,
    {
      data: products,
      isUninitialized: isProductsUninitialized,
      isFetching: isProductsFetching,
      status: productsQueryStatus,
    },
  ] = useLazyProductsQuery()

  const { data: totalProducts, isSuccess: isTotalProductsSuccess } =
    useTotalProductCountQuery()

  const { data: inFreezePeriod } = useGetIsInFreezePeriodQuery()

  const {
    data: listSavedProductFilters,
    isLoading: isListSavedProductFiltersLoading,
  } = useListSavedProductFiltersQuery({})

  const [requestParams, setRequestParams] = useReducer(
    (
      state: ProductsRequestParamsType,
      action: { type?: string; payload?: any },
    ) => {
      let newState = { ...state }

      switch (action.type) {
        case 'filter':
          const nonFilterState = nonFilterFields.reduce(
            (acc: Record<string, any>, f: string) => {
              const v = state[f as keyof ProductsRequestParamsType]
              if (v !== undefined) acc[f] = v
              return acc
            },
            {},
          )
          newState = {
            ...nonFilterState,
            ...fixFilterFieldValues(
              new URLSearchParams(action.payload),
              action.payload,
            ),
            page: 1,
          }
          break
        case 'pagination':
          if (action.payload.incr === 'next') {
            newState = { ...state, page: Number(state?.page) + 1 }
            break
          }
          if (action.payload.incr === 'prev') {
            newState = { ...state, page: Number(state?.page) - 1 }
            break
          }
          newState = { ...state, page: action.payload.pageNumber }
          break
        case 'sort':
          newState = { ...state, ...action.payload, page: 1 }
          break
        case 'search':
          newState = { ...state, search: action.payload, page: 1 }
          break
        case 'reset':
          newState = { ...initialRequestParams, limit: state.limit }
          break
        case 'sync':
          const syncParams = new URLSearchParams(action.payload)
          newState = {
            ...(syncParams.size > 0
              ? fixFilterFieldValues(
                  syncParams,
                  Object.fromEntries(syncParams.entries()),
                )
              : initialRequestParams),
          }
          break
      }

      const newStateFields = Object.keys(newState)
      if (
        !Object.keys(initialRequestParams).every((k) =>
          newStateFields.includes(k),
        )
      ) {
        newState = { ...initialRequestParams, ...newState }
      }

      if (
        (action.type === 'sync' && !!queryString) ||
        (action.type === 'reset' && !action.payload.refetch)
      ) {
        return newState
      }

      const newQueryParams = new URLSearchParams()
      Object.entries(newState).forEach(([key, value]) => {
        if (value) {
          newQueryParams.append(key, `${value}`)
        }
      })

      if (`?${newQueryParams.toString()}` !== queryString) {
        navigate(
          `.?${newQueryParams.toString()}${isDebug ? `&${Object.keys(debugParam)[0]}` : ''}`,
          {
            relative: 'path',
            replace: true,
          },
        )
      }

      return newState
    },
    initialRequestParams,
  )

  // Filter panel state
  const [panelVisible, setPanelVisible] = useState<boolean | null>(null)
  const [selectedQuickFilter, setSelectedQuickFilter] = useState<{
    filterItem: Partial<Filter> | null
    filterContent: FilterFormType | null
  } | null>(null)

  const filterFieldValues = useMemo(() => {
    const filtersClone = { ...(requestParams as ProductsFilterType) }
    nonFilterFields.forEach((field: string) => {
      delete filtersClone[field as keyof typeof filtersClone]
    })
    return filtersClone
  }, [requestParams])

  const shouldScrollRef = useRef(false)

  const fetchProductsMemoized = useCallback(
    (preferCache = true) => {
      const queryParams = new URLSearchParams(queryString)
      if (queryParams.size === 0) {
        return
      }
      const args = Object.fromEntries(queryParams.entries())

      if (isDebug) {
        delete args[Object.keys(debugParam)[0]]
        args['isDebug'] = 'true'
      }

      fetchProducts(args as unknown as PaginatedQueryParams, preferCache)
      shouldScrollRef.current = true
    },
    [queryString, isDebug],
  )

  const onUpdateFilters = (newFilters: ProductsFilterType): void => {
    setRequestParams({ type: 'filter', payload: newFilters })
    if (selectedQuickFilter?.filterItem?.id) {
      setSelectedQuickFilter((prev) =>
        prev ? { ...prev, filterContent: { ...newFilters } } : null,
      )
    }
  }

  const onUpdatePagination =
    (incr?: 'next' | 'prev') =>
    (pageNumber?: number): void => {
      setRequestParams({ type: 'pagination', payload: { incr, pageNumber } })
    }

  const onSort = (sortBy: { field: string; isDesc: boolean }): void => {
    setRequestParams({
      type: 'sort',
      payload: {
        orderBy: sortBy.field,
        orderDirection: sortBy.isDesc ? 'desc' : 'asc',
      },
    })
  }

  const handleFilterSearch = (s: string): void => {
    setRequestParams({ type: 'search', payload: s })
  }

  const resetFilters = (refetch = false): void => {
    setRequestParams({ type: 'reset', payload: { refetch } })
  }

  const onFilterOpenClick = (): void => {
    const hasSelectedQuickFilter = !!selectedQuickFilter?.filterItem
    if (hasSelectedQuickFilter) {
      onResetFilters()
    }
    setPanelVisible((current) => {
      if (current && hasSelectedQuickFilter) {
        return current
      }
      return !current
    })
  }

  const onResetFilters = (refetch = false): void => {
    setSelectedQuickFilter(null)
    resetFilters(refetch)
  }

  const onSaveQuickFilter = (values: any, url?: string): void => {
    const cloned = { ...values }
    nonFilterFields.forEach((field: string) => {
      delete cloned[field as keyof typeof cloned]
    })
    values = cloned
    setSelectedQuickFilter((prev) => ({
      filterItem: {
        ...(prev?.filterItem || {}),
        url: url,
      },
      filterContent: { ...values },
    }))
    onUpdateFilters(values)
  }

  const onSelectSavedFilter = (filter: any): void => {
    setPanelVisible(true)
    if (selectedQuickFilter?.filterItem?.id === filter.id) {
      return
    }

    let params: any = {}
    try {
      params = JSON.parse(filter.url)
    } catch {
      return
    }
    const newFilters: Record<string, string> = {}
    // TODO: Old-app compatibility - remove this after the data is fixed
    for (const paramKey in params) {
      const key = paramKey.replaceAll('-', '_')

      if (
        params?.['tag'] &&
        key === 'tag_filter_operator' &&
        params[paramKey] === ''
      ) {
        params[key] = 'and'
      }

      const value = params[key] ?? params[paramKey]
      if (value !== undefined && value !== null) {
        newFilters[key] = typeof value === 'string' ? value : `${value}`
      }
    }

    setSelectedQuickFilter({ filterItem: filter, filterContent: newFilters })
    onUpdateFilters(newFilters)
  }

  useEffect(() => {
    if (selectedQuickFilter?.filterItem?.id) {
      const filtersClone = { ...(requestParams as ProductsFilterType) }
      const selectedQuickFilterClone = { ...selectedQuickFilter.filterContent }
      const decimalFixFields = ['price_min', 'price_max']
      nonFilterFields.forEach((field: string) => {
        delete filtersClone[field as keyof typeof filtersClone]
      })
      // TODO: Old-app compatibility - remove this after the data is fixed
      decimalFixFields.forEach((priceFilter: string) => {
        if (filtersClone?.[priceFilter as 'price_min' | 'price_max']) {
          filtersClone[priceFilter as 'price_min' | 'price_max'] = parseFloat(
            `${filtersClone[priceFilter as 'price_min' | 'price_max']}`,
          )
        }
      })
      // TODO: Old-app compatibility - remove this after the data is fixed
      for (const key in selectedQuickFilterClone) {
        if (
          selectedQuickFilterClone?.[key as keyof ProductsFilterType] ===
            undefined ||
          selectedQuickFilterClone?.[key as keyof ProductsFilterType] ===
            null ||
          selectedQuickFilterClone?.[key as keyof ProductsFilterType] === ''
        ) {
          delete selectedQuickFilterClone[key as keyof ProductsFilterType]
        } else if (decimalFixFields.includes(key)) {
          selectedQuickFilterClone[key as keyof ProductsFilterType] =
            parseFloat(
              `${selectedQuickFilterClone[key as keyof ProductsFilterType]}`,
            )
        } else if (key === 'search') {
          // TODO filter url includes search attr but this is not a valid filter attr. Will be fixed in new filter
          delete selectedQuickFilterClone['search' as keyof ProductsFilterType]
        }
      }
      if (
        Object.keys(diff(selectedQuickFilterClone, filtersClone)).length > 0
      ) {
        setSelectedQuickFilter(null)
      }
    }
    if (panelVisible === null && Object.keys(filterFieldValues).length > 0) {
      setPanelVisible(true)
    }
  }, [requestParams])

  useEffect(() => {
    setRequestParams({ type: 'sync', payload: queryString })
    fetchProductsMemoized()
  }, [queryString])

  useEffect(() => {
    if (
      shouldScrollRef.current &&
      [QueryStatus.fulfilled, QueryStatus.rejected].includes(
        productsQueryStatus,
      )
    ) {
      scrollToId('product-list')
      shouldScrollRef.current = false
    }
  }, [products, productsQueryStatus])

  useEffect(() => {
    trackEventInternal(IntercomEvent['panel-visited'])
  }, [])

  const hasFilters = useMemo(() => {
    const queryParamsArr = Array.from(new URLSearchParams(queryString).keys())
    return queryParamsArr.some((f) => !nonFilterFields.includes(f))
  }, [queryString])

  return (
    <FastFetchModalProvider>
      <EditProductProvider modalContent={<EditProductModal />}>
        <DeleteProductProvider modalContent={<DeleteProductModal />}>
          <RemoveUrlProvider modalContent={<RemoveUrlModal />}>
            <ReportUrlProvider modalContent={<ReportUrlModal />}>
              <AlreadyReportedUrlProvider
                modalContent={<AlreadyReportedModal />}
              >
                <ToggleExclusionProvider
                  modalContent={<ToggleExclusionModal />}
                >
                  <Wrapper>
                    <AddUrlFastFetchModal />
                    <ProductHeader
                      productCount={products?.meta.totalItems}
                      totalProducts={
                        hasFilters && isTotalProductsSuccess
                          ? totalProducts
                          : undefined
                      }
                      isInitiated={!isProductsUninitialized}
                      isLoading={isProductsFetching}
                    />
                    <div className="flex flex-col lg:flex-row pt-8">
                      <div className="lg:w-8/12 flex order-last lg:order-1 flex-col">
                        <ProductSearch
                          isInitiated={!isProductsUninitialized}
                          onSort={onSort}
                          sortBy={{
                            field: requestParams?.orderBy ?? '',
                            isDesc: requestParams?.orderDirection === 'desc',
                          }}
                          onSearch={handleFilterSearch}
                          foundedProducts={
                            (products as unknown as PaginatedResponse<Product>)
                              ?.meta.totalItems
                          }
                          filters={requestParams as ProductsFilterType}
                          panelVisible={panelVisible}
                          onFilterOpenClick={onFilterOpenClick}
                          listSavedProductFilters={listSavedProductFilters}
                          isListSavedProductFiltersLoading={
                            isListSavedProductFiltersLoading
                          }
                          selectedQuickFilterId={
                            selectedQuickFilter?.filterItem?.id
                          }
                          selectedQuickFilterName={
                            selectedQuickFilter?.filterItem?.name
                          }
                          onSelectSavedFilter={onSelectSavedFilter}
                          onApplyFilters={onUpdateFilters}
                          onResetFilters={onResetFilters}
                          onSaveFilter={onSaveQuickFilter}
                          setPanelVisible={setPanelVisible}
                        />
                        <div id="product-list">
                          <ProductsList
                            isInitiated={!isProductsUninitialized}
                            isLoading={isProductsFetching}
                            fetchStatus={productsQueryStatus}
                            products={products}
                            hasFilters={hasFilters}
                            isInFreezePeriod={inFreezePeriod}
                            fetcher={fetchProductsMemoized}
                          />
                        </div>
                        <div className="border-t border-solid border-gray-200 mb-14 mt-6 lg:mb-0">
                          <NumberedPagination
                            currentPage={requestParams.page as number}
                            meta={products?.meta}
                            onNext={onUpdatePagination('next')}
                            onPrev={onUpdatePagination('prev')}
                            onSelect={onUpdatePagination()}
                            isInitiated={!isProductsUninitialized}
                          />
                        </div>
                      </div>
                      <div className="lg:w-4/12 flex flex-col lg:order-2 lg:pl-4">
                        <ProductsSidebar />
                      </div>
                    </div>
                  </Wrapper>
                </ToggleExclusionProvider>
              </AlreadyReportedUrlProvider>
            </ReportUrlProvider>
          </RemoveUrlProvider>
        </DeleteProductProvider>
      </EditProductProvider>
    </FastFetchModalProvider>
  )
}

export default Product
